/**
 * Query step component
 * Step 1 of job wizard - search query input
 */

import { useFormContext, Controller } from 'react-hook-form';
import { Input, Typography, Space, Tag, Alert } from 'antd';
import { SearchOutlined, BulbOutlined, HistoryOutlined } from '@ant-design/icons';
import { usePreferencesStore } from '@/stores/usePreferencesStore';
import type { CreateJobInput } from '@/types/job';

const { Title, Text, Paragraph } = Typography;

const EXAMPLE_QUERIES = [
  'restaurants',
  'hotels',
  'coffee shops',
  'dentists',
  'gyms',
  'pharmacies',
  'car repair',
  'hair salons',
];

const TIPS = [
  'Be specific for better results (e.g., "italian restaurants" instead of just "restaurants")',
  'Include qualifiers like "open 24 hours" or "with parking"',
  'Use business categories that appear on Google Maps',
  'Avoid using location names - that will be set in the next step',
];

export function QueryStep() {
  const {
    control,
    setValue,
    formState: { errors },
  } = useFormContext<CreateJobInput>();

  const { recentSearches, addRecentSearch } = usePreferencesStore();

  const handleExampleClick = (query: string) => {
    setValue('searchQuery', query, { shouldValidate: true });
  };

  const handleRecentClick = (query: string) => {
    setValue('searchQuery', query, { shouldValidate: true });
  };

  return (
    <div style={{ maxWidth: 600, margin: '0 auto' }}>
      <Title level={4} style={{ marginBottom: 8 }}>
        What do you want to search for?
      </Title>
      <Paragraph type="secondary" style={{ marginBottom: 24 }}>
        Enter a search query as you would on Google Maps.
      </Paragraph>

      {/* Main search input */}
      <Controller
        name="searchQuery"
        control={control}
        render={({ field }) => (
          <Input
            {...field}
            size="large"
            prefix={<SearchOutlined />}
            placeholder="e.g., restaurants, hotels, coffee shops..."
            status={errors.searchQuery ? 'error' : undefined}
            style={{ marginBottom: 8 }}
          />
        )}
      />
      {errors.searchQuery && (
        <Text type="danger" style={{ fontSize: 13 }}>
          {errors.searchQuery.message}
        </Text>
      )}

      {/* Optional name field */}
      <div style={{ marginTop: 24 }}>
        <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
          Job name (optional)
        </Text>
        <Controller
          name="name"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              placeholder="Give this job a descriptive name..."
              style={{ marginBottom: 8 }}
            />
          )}
        />
      </div>

      {/* Recent searches */}
      {recentSearches.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <Space size={4} style={{ marginBottom: 8 }}>
            <HistoryOutlined />
            <Text type="secondary">Recent searches:</Text>
          </Space>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {recentSearches.slice(0, 8).map((query, index) => (
              <Tag
                key={index}
                onClick={() => handleRecentClick(query)}
                style={{ cursor: 'pointer' }}
              >
                {query}
              </Tag>
            ))}
          </div>
        </div>
      )}

      {/* Example queries */}
      <div style={{ marginTop: 24 }}>
        <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
          Popular examples:
        </Text>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {EXAMPLE_QUERIES.map((query) => (
            <Tag
              key={query}
              color="blue"
              onClick={() => handleExampleClick(query)}
              style={{ cursor: 'pointer' }}
            >
              {query}
            </Tag>
          ))}
        </div>
      </div>

      {/* Tips */}
      <Alert
        style={{ marginTop: 32 }}
        type="info"
        showIcon
        icon={<BulbOutlined />}
        message="Tips for better results"
        description={
          <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
            {TIPS.map((tip, index) => (
              <li key={index} style={{ marginBottom: 4 }}>
                {tip}
              </li>
            ))}
          </ul>
        }
      />
    </div>
  );
}

export default QueryStep;
